import { FC, useCallback } from 'react'
import classNames from 'classnames'
import { LinkButton } from '@/atoms/Button'
import { CircleInfoIcon } from '@/atoms/Icons/CircleInfoIcon'
import { GiftBowIcon } from '@/atoms/Icons/GiftBowIcon'
import { GuildIconMono } from '@/atoms/Icons/GuildIconMono'
import { PlayCircleOutlineIcon } from '@/atoms/Icons/PlayCircleOutlineIcon'
import { ShareOutlineIcon } from '@/atoms/Icons/ShareOutlineIcon'
import { TicketIcon } from '@/atoms/Icons/TicketIcon'
import { LabelMD } from '@/atoms/Text'
import { IconColor } from '@/atoms/utils'
import { paths } from '@/constants/paths'
import { WatchlistButton } from '@/molecules/WatchlistButton'
import { ProjectHoverContent } from '@/services/ProjectsService/types'
import { useSafeTrack } from '@/utils/analytics'

interface CtaButtonsProps {
  className?: string
  hoverContent?: ProjectHoverContent | null
  isGuildMember?: boolean
  isInTheaters?: boolean
  currentlyStreaming?: boolean
  isGuildGated?: boolean
  canSeeGuildShareButton?: boolean
  shareCtaPath?: string | null
  isLoggedIn?: boolean
}

interface CtaContent {
  primaryCtaText?: string | null
  primaryCtaPath?: string | null
  primaryCtaIcon?: string | null
  secondaryCtaText?: string | null
  secondaryCtaPath?: string | null
  secondaryCtaIcon?: string | null
}

export const CtaButtons: FC<CtaButtonsProps> = ({
  className,
  hoverContent,
  isGuildMember = false,
  isInTheaters = false,
  currentlyStreaming = false,
  isGuildGated = false,
  canSeeGuildShareButton = false,
  shareCtaPath = null,
  isLoggedIn = false,
}) => {
  const track = useSafeTrack()

  const handleButtonClick = useCallback(
    (ctaPath?: string | null) => {
      track('Watch Hover CTA Clicked', {
        ctaPath,
        projectSlug: hoverContent?.project?.slug,
        contentType: hoverContent?.__typename,
      })
    },
    [hoverContent?.__typename, hoverContent?.project?.slug, track],
  )

  const getCtaContent = (): CtaContent => {
    const projectSlug = hoverContent?.project?.slug ?? ''
    const contentType = hoverContent?.__typename
    const resumeWatching = Boolean(
      hoverContent && 'watchPosition' in hoverContent && hoverContent?.watchPosition?.position,
    )

    switch (contentType) {
      case 'ContentSpecial':
        return createCta(getWatchText(resumeWatching), `/watch/${projectSlug}`, 'play')

      case 'ContentMovie':
        return getMovieCta(
          projectSlug,
          resumeWatching,
          isInTheaters,
          currentlyStreaming,
          isGuildMember,
          canSeeGuildShareButton,
          shareCtaPath,
          hoverContent?.id ?? null,
        )

      case 'ContentEpisode': {
        const watchText = getWatchText(resumeWatching)
        const episodePath = hoverContent?.id
          ? `/watch/${projectSlug}/episode/${hoverContent.id}`
          : `/watch/${projectSlug}`
        return createCta(watchText, episodePath, 'play')
      }

      case 'ContentSeries':
        return getSeriesCta(
          projectSlug,
          resumeWatching,
          isGuildGated,
          isGuildMember,
          hoverContent?.firstWatchable as FirstEpisode,
        )

      default:
        return createCta('Details', `/shows/${projectSlug}`, 'info')
    }
  }

  const ctaContent = getCtaContent()

  if (!ctaContent.primaryCtaText && !ctaContent.primaryCtaPath) return null
  //TODO add translations for this component if this wins

  return (
    <div className={classNames('flex flex-col gap-2 md:flex-row md:gap-4 my-6', className)}>
      {ctaContent.primaryCtaPath && ctaContent.primaryCtaText && (
        <LinkButton
          href={ctaContent.primaryCtaPath}
          variant="white"
          onClick={() => handleButtonClick(ctaContent.primaryCtaPath)}
          className="mb-4 flex max-h-12 w-full items-center gap-[6px] rounded-xl px-6 py-[14px] md:w-fit"
        >
          {ctaContent?.primaryCtaIcon && determineButtonIcon(ctaContent.primaryCtaIcon, 'core-gray-950')}
          <LabelMD weight="semibold" className="mt-[2.25px]">
            {ctaContent.primaryCtaText}
          </LabelMD>
        </LinkButton>
      )}

      {ctaContent.secondaryCtaPath && ctaContent.secondaryCtaText && (
        <LinkButton
          href={ctaContent.secondaryCtaPath}
          variant="core-gray-800"
          onClick={() => handleButtonClick(ctaContent.secondaryCtaPath)}
          className="mb-4 flex max-h-12 w-full items-center gap-[6px] rounded-xl bg-core-gray-500 px-6 py-[14px] backdrop-blur-3xl md:w-fit"
        >
          {ctaContent?.secondaryCtaIcon && determineButtonIcon(ctaContent.secondaryCtaIcon, 'white')}
          <LabelMD weight="semibold" className="mt-[2.25px]">
            {ctaContent.secondaryCtaText}
          </LabelMD>
        </LinkButton>
      )}
      {hoverContent?.id && isLoggedIn && <WatchlistButton titleId={hoverContent.id} />}
    </div>
  )
}

const determineButtonIcon = (icon: string, color: IconColor) => {
  switch (icon) {
    case 'play':
      return <PlayCircleOutlineIcon size={24} color={color} />
    case 'ticket':
      return <TicketIcon size={24} color={color} />
    case 'gift':
      return <GiftBowIcon size={24} color={color} />
    case 'share':
      return <ShareOutlineIcon size={24} color={color} />
    case 'info':
      return <CircleInfoIcon size={24} color={color} />
    case 'guild':
      return <GuildIconMono size={24} color={color} />
    default:
      return null
  }
}

const createCta = (
  primaryCtaText: string,
  primaryCtaPath: string,
  primaryCtaIcon: string,
  secondaryCtaText?: string | null,
  secondaryCtaPath?: string | null,
  secondaryCtaIcon?: string | null,
): CtaContent => ({
  primaryCtaText,
  primaryCtaPath,
  primaryCtaIcon,
  secondaryCtaText: secondaryCtaText || null,
  secondaryCtaPath: secondaryCtaPath || null,
  secondaryCtaIcon: secondaryCtaIcon || null,
})

const getWatchText = (resumeWatching: boolean): string => (resumeWatching ? 'Resume' : 'Watch Now')

const getMovieCta = (
  projectSlug: string,
  resumeWatching: boolean,
  isInTheaters: boolean,
  currentlyStreaming: boolean,
  isGuildMember: boolean,
  canSeeGuildShareButton: boolean,
  shareCtaPath: string | null,
  id?: string | null,
): CtaContent => {
  if (isInTheaters) {
    return createCta('Get Showtimes', `/tickets/${projectSlug}`, 'ticket')
  }

  if (currentlyStreaming) {
    const watchText = getWatchText(resumeWatching)
    const watchPath = id ? `/watch/${projectSlug}/episode/${id}` : `/watch/${projectSlug}`

    if (isGuildMember && canSeeGuildShareButton && shareCtaPath) {
      return createCta(watchText, watchPath, 'play', 'Share This Film', shareCtaPath, 'share')
    }

    return createCta(watchText, watchPath, 'play')
  }

  return createCta('Details', `/movies/${projectSlug}`, 'info')
}

interface FirstEpisode {
  id?: string
  episodeNumber?: number
  season?: {
    seasonNumber?: number
  }
}

const getSeriesCta = (
  projectSlug: string,
  resumeWatching: boolean,
  isGuildGated: boolean,
  isGuildMember: boolean,
  firstEpisode?: FirstEpisode | null,
): CtaContent => {
  if (isGuildGated && !isGuildMember) {
    return createCta('Join to Watch', `${paths.guild.join}/${projectSlug}`, 'guild')
  }

  if (
    firstEpisode &&
    'episodeNumber' in firstEpisode &&
    firstEpisode?.episodeNumber &&
    firstEpisode?.season?.seasonNumber
  ) {
    const episodeText = `Watch S${firstEpisode.season.seasonNumber} E${firstEpisode.episodeNumber}`
    const episodePath = `/watch/${projectSlug}/episode/${firstEpisode.id}`
    return createCta(episodeText, episodePath, 'play')
  }

  const watchText = getWatchText(resumeWatching)
  return createCta(watchText, `/watch/${projectSlug}`, 'play')
}
